import { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone;
    if (isStandalone) return;

    // Only show on mobile devices
    const isMobile = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (!isMobile) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return; // 7 days
    }

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    // Android/Desktop: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowPrompt(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS: show custom prompt after delay
    if (isiOS) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-4 left-4 right-4 z-[9999] sm:left-auto sm:right-4 sm:max-w-sm"
        data-testid="pwa-install-prompt"
      >
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-teal-500/30 rounded-2xl p-5 shadow-2xl shadow-teal-500/10">
          <button
            onClick={handleDismiss}
            className="absolute top-3 right-3 text-slate-500 hover:text-white transition-colors"
            data-testid="pwa-dismiss"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center shrink-0">
              <Smartphone className="w-6 h-6 text-teal-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-semibold text-sm">Install eSIM4You</h3>
              {isIOS ? (
                <p className="text-slate-400 text-xs mt-1 leading-relaxed">
                  Tap <span className="inline-flex items-center"><svg className="w-3.5 h-3.5 mx-0.5 text-teal-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 12h16M12 4l-5 8h10l-5 8"/><rect x="4" y="16" rx="1" width="16" height="4"/></svg></span> then <strong className="text-teal-400">"Add to Home Screen"</strong>
                </p>
              ) : (
                <>
                  <p className="text-slate-400 text-xs mt-1">Quick access to your eSIMs</p>
                  <button
                    onClick={handleInstall}
                    className="mt-3 flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-black font-semibold text-xs px-4 py-2 rounded-lg transition-colors"
                    data-testid="pwa-install-button"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Install App
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
